import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { FaceCapture } from '../../types';
import { ScanFace, Loader2, X } from 'lucide-react';

// Faces gallery — the clearest face captured per event, the same way we keep the
// best-frame cover for person/vehicle. Each confirmed person/stranger event
// carries a tight best-frame face crop (events/.../face.jpg) beside the wider
// scene cover; this lists them newest first, filterable by look-back window and
// camera, with the scene on click. Just another detection surface — retention
// follows the normal alert/recording policy, nothing special.
const WINDOWS: { label: string; days: number }[] = [
  { label: '24 hours', days: 1 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
];

export const FacesPage: React.FC = () => {
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState<FaceCapture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cam, setCam] = useState('');
  const [scene, setScene] = useState<FaceCapture | null>(null);

  const load = useCallback(async (d: number) => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.listFaceCaptures({ days: d, limit: 300 }));
    } catch {
      setError('Could not load captured faces.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const shown = rows.filter((r) => !cam || r.vuid.toLowerCase().includes(cam.toLowerCase()));
  const fmt = (iso: string) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ScanFace size={20} color="var(--primary-light)" />
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-main)' }}>Faces</h2>
            <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>Clearest face captured per stranger event · newest first</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={cam}
            onChange={(e) => setCam(e.target.value)}
            placeholder="Filter by camera serial…"
            style={{ background: 'var(--bg-surface, rgba(255,255,255,0.03))', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '7px 10px', fontSize: 13, color: 'var(--text-main)', minWidth: 200 }}
          />
          <div style={{ display: 'flex', gap: 4, background: 'var(--bg-surface, rgba(255,255,255,0.03))', borderRadius: 'var(--radius-md)', padding: 3 }}>
            {WINDOWS.map((w) => (
              <button key={w.days} onClick={() => setDays(w.days)}
                className={`btn btn-sm ${days === w.days ? 'btn-primary' : 'btn-secondary'}`}
                style={{ fontSize: 12 }}>{w.label}</button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={20} className="spin" /></div>
      ) : error ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--danger-text, #f87171)', fontSize: 13 }}>{error}</div>
      ) : shown.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>
          No captured faces in this window{cam ? ' for that camera' : ''}.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
          {shown.map((r) => (
            <button key={r.id} onClick={() => r.coverUrl && setScene(r)} title={r.coverUrl ? 'View scene' : undefined}
              style={{ textAlign: 'left', padding: 0, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--bg-surface-card)', cursor: r.coverUrl ? 'pointer' : 'default' }}>
              <div style={{ position: 'relative', aspectRatio: '1 / 1', background: '#0d0d0d' }}>
                <img src={r.faceUrl} alt={`Face ${r.vuid}`} loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 999, background: r.known ? 'rgba(52,211,153,0.9)' : 'rgba(248,113,113,0.92)', color: '#07140f' }}>
                  {r.known ? (r.name || 'Known') : 'Unknown'}
                </span>
              </div>
              <div style={{ padding: '7px 9px' }}>
                <div style={{ fontSize: 11.5, color: 'var(--text-main)', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.vuid}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>{fmt(r.at)}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Scene (cover) lightbox */}
      {scene && (
        <div onClick={() => setScene(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', maxWidth: 820, width: '100%', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 13, color: 'var(--text-main)' }}>
                <span style={{ fontFamily: 'monospace' }}>{scene.vuid}</span> · {fmt(scene.at)} · {scene.known ? (scene.name || 'Known') : 'Unknown person'}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setScene(null)}><X size={14} /></button>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
              <img src={scene.faceUrl} alt="Face crop" style={{ maxHeight: 260, borderRadius: 8, border: '1px solid var(--border-subtle)' }} />
              {scene.coverUrl && <img src={scene.coverUrl} alt="Scene" style={{ maxHeight: 260, maxWidth: '100%', borderRadius: 8, border: '1px solid var(--border-subtle)' }} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
